import { Injectable } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreDocument,
} from '@angular/fire/compat/firestore';

import firebase from 'firebase/compat/app';
import {
  BehaviorSubject,
  Observable,
  Subject,
} from 'rxjs';
import { tap } from 'rxjs/operators';

import { AnalyticsService } from '../../../core/services/analytics/analytics.service';
import { GuardianSyncJob } from '../../guardians/interfaces/guardian-sync.model';
import { RosterSyncJob } from '../../roster-sync-job/interfaces/roster-sync-jobs.model';
import { ActiveJobStatus } from '../interfaces/active-job-progress.model';

import Timeout = NodeJS.Timeout;

@Injectable({
  providedIn: 'root'
})
export class ActiveJobProgressService {

  public jobInProgress$ = new BehaviorSubject<string>(null);
  public jobCanceled$ = new Subject<boolean>();
  public cancelRequested = false;
  public indeterminateMode = false;
  private backoffCountdown$ = new Subject<number>();
  private interval: Timeout;
  private customerId: string;
  private jobStatus: AngularFirestoreDocument<ActiveJobStatus>;
  constructor(private afs: AngularFirestore,
              private analyticsService: AnalyticsService) { }

  getActiveJobForCustomer(customerId: string): Observable<ActiveJobStatus> {
    this.customerId = customerId;
    this.jobStatus = this.afs.doc<ActiveJobStatus>(`/customers/${customerId}/sync/active/meta/status`);
    return this.jobStatus.valueChanges().pipe(
      tap((status) => {
        if (status && status.phaseIndex === 1) {
          this.cancelRequested = false;
        }
        if (status) {
          // console.log('id', status.id);
          if(!status.id || status.id === 'GUARDIAN_SYNC'){
            this.indeterminateMode = true;
            if (!status.id) {
              status.id = 'unknown';
            }
          }
        }
        this.jobInProgress$.next(status ? status.id : null);
      }),
      tap((status) => this.handleBackoff_(status))
    );
  }

  getBackoffCountdown(): Observable<number> {
    return this.backoffCountdown$.asObservable();
  }

  resetService(): void{
    this.afs.doc(`/customers/${this.customerId}/sync/active`).set({
      cancel: false
    }, {merge: true}).then(() => {
      this.cancelRequested = false;
    }).catch(error => console.error(error));

  }

  cancelActiveJob() {
    this.jobCanceled$.next(true);
    this.cancelRequested = true;
    return this.afs.doc(`/customers/${this.customerId}/sync/active`).set({
      cancel: true,
      cancelRequested: firebase.firestore.FieldValue.serverTimestamp()
    }, {merge: true});
  }

  setJobRunning(job: RosterSyncJob | GuardianSyncJob | null, indeterminate = false): void {
    this.indeterminateMode = indeterminate;
    this.jobInProgress$.next(job ? job.id: null);
  }

  private handleBackoff_(status: ActiveJobStatus): void {
    if (status && 'backoffDelay' in status) {
      if (this.interval) {
        clearInterval(this.interval);
      }
      let currentDelay = status.backoffDelay;
      this.backoffCountdown$.next(status.backoffDelay);
      this.interval = setInterval(() => {
        currentDelay--;
        this.backoffCountdown$.next(currentDelay);
        if (currentDelay < 1) {
          clearInterval(this.interval);
          this.interval = null;

          try {
            // @ts-ignore Ignore the mismatch between type number and type FieldValue
            this.jobStatus.update({backoffDelay: firebase.firestore.FieldValue.delete()});
          } catch (e) {
            this.analyticsService.sendEvent({
              action: 'error_detected',
              properties: {
                category: 'backoff_countdown_cleanup',
                label: this.customerId
              }
            });
          }
        }
      }, 1000);
    } else {
      this.backoffCountdown$.next(null);
      if (this.interval) {
        clearInterval(this.interval);
      }
    }
  }
}
